import { Honeybadger } from '@honeybadger-io/react';
import { signOut, getSession } from "next-auth/react";
import { filterLinksFromPermissions } from "./permissions";
import { isPayerPage } from "./payerPages";

// Guard so concurrent 401s (e.g. several SWR requests failing at once) only kick
// off a single sign-out + redirect instead of racing.
let isHandlingUnauthorized = false;
// Same idea for 403s: a signed-in user hitting a page they lack permission for.
let isHandlingForbidden = false;

export const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again and contact us at help@streamlinedpayments.com if the problem continues.";

// do not log API errors to honeybadger, only log Next.js server-side errors and client errors
const isWhiteListError = (err) => {
    // Always whitelist errors from buildErrors (API errors)
    // These are identified by having a fnName property or being in the context
    if (err?.fnName || err?.context?.fnName) {
        return true;
    }

    // For other errors, only whitelist specific status codes
    const statusCode = err?.statusCode || err?.status;
    const whitelistedCodes = ['401', '404', '422', '403', '429', '500'];

    return statusCode && whitelistedCodes.includes(statusCode.toString());
}

// Handle errors that are received from the Rails Backend
export const buildErrors = (e, fnName, context) => {
    const {
        statusCode: status,
        body: data,
    } = e.response || {};

    if (e.message?.includes('Cannot read property \'accessToken\' of null')) {
        return [
            401,
            {
                type: 'error',
                status: 401,
                message: "Session expired. Please sign in."
            }
        ];
    }


    if(!e.request && !e.response){ // No request was made and no response from BE (Error in intermediate step)
        return [
            500,
            {
                type: 'error',
                status: 500,
                message: GENERIC_ERROR_MESSAGE
            }
        ]
    }

    if (!status && e.request) { // BE is down (only show 503 if request was made and there is no response)
        return [
            503,
            {
                type: 'error',
                status: 503,
                message: "The application is temporarily unavailable."
            }
        ];
    }
    if (status === 404) {
        return [
            status,
            {
                type: 'error',
                status: status,
                message: e.response.body?.message
            }
        ];
    }

    if (data && data?.errors) {
        const errors = data.errors;
        errors.map((error) => {
            console.error(error);
            console.error(error.statusCode, error.statusMessage, fnName);
            // Only set type property if error is an object, not a string
            if (typeof error === 'object') {
                error.type = 'error';
                if (error.hasOwnProperty('messages')) {
                    error.message = error.messages;
                }
            }
        });
        !isWhiteListError(errors[0]) && Honeybadger.notify(errors[0], {
            context: {
                ...context,
                fnName,
                error: {
                    status: errors[0]?.statusCode,
                    statusText: errors[0]?.statusMessage
                },
                fullError: data
            }
        });
        // A plain string message has nowhere to carry the backend's code (e.g.
        // shopify_location_required), so wrap it when there is one. Without a code
        // the string passes through as it always has.
        if (typeof errors[0] !== 'object' && data.code) {
            return [
                status,
                { type: 'error', status, message: errors[0], code: data.code }
            ];
        }
        return [
            status,
            errors[0]
        ];
    }

    if (data && data?.error) {
        const error = data.error;
        // Log the fields, not e.response itself: that's a Node IncomingMessage, and
        // printing it dumps hundreds of lines that Vercel files as a runtime error.
        console.error(
            e.response.statusCode,
            e.response.statusMessage,
            e.response.request?.options?.method,
            e.response.requestUrl?.pathname,
            error,
            fnName
        );
        !isWhiteListError(e.response) && Honeybadger.notify(e.response.statusCode, {
            context: {
                ...context,
                fnName,
                error: {
                    status: e.response?.statusCode,
                    statusText: e.response?.statusMessage
                },
                fullError: data
            }
        });
        return [
            status,
            {
                type: 'error',
                status: status,
                message: error,
                // Machine-readable code from the backend (e.g. not_payable,
                // no_payment_method). Callers branch on it or map it onto a form
                // field; the human message alone can't be targeted. Spread so
                // responses without one are unchanged.
                ...(data.code ? { code: data.code } : {}),
                // Why the code applies, when the backend can say (e.g. no_payment_method
                // because every method is kept to invoices).
                ...(data.reason ? { reason: data.reason } : {}),
            }
        ];
    }

    // Fallback for empty or unexpected error responses (e.g., timeout, 500 with no body)
    return [
        status || 500,
        {
            type: 'error',
            status: status || 500,
            message: GENERIC_ERROR_MESSAGE
        }
    ];
};

// Read a response body as JSON without letting a non-JSON body crash the caller. An
// empty successful body (a 204, a DELETE answered with nothing) reads as {}. An empty
// failed body, or one that isn't JSON (an HTML error page from the platform, a proxy
// 502/504, a WAF challenge), throws our usual error shape instead of {} or
// "Unexpected token '<'", so a caller showing the error always has a message. Anything
// else, such as a connection dropped mid-body, isn't a parse problem and is rethrown.
//
// Empty is judged from the status and Content-Length rather than by reading text():
// Node sets Content-Length: 0 on an empty response, and fetch mocks across the test
// suite only implement json().
export const parseJsonBody = async (response) => {
    const genericError = { type: 'error', status: response.status, message: GENERIC_ERROR_MESSAGE };
    if (response.status === 204 || response.headers?.get?.('content-length') === '0') {
        if (!response.ok) throw genericError;
        return {};
    }
    try {
        return await response.json();
    } catch (error) {
        if (!(error instanceof SyntaxError)) throw error;
        // Path only, matching buildErrors: query strings can carry tokens.
        const path = response.url ? new URL(response.url).pathname : undefined;
        console.error('Non-JSON response', response.status, response.headers?.get?.('content-type'), path);
        throw genericError;
    }
};

// Process and display errors on the frontend UI
// A 401 or 403 never signs out or redirects on sign-in/out, where it would loop, or on a payer
// page, where it's a rejected link token rather than the signed-in merchant's session.
const isPublicPage = (path) => path.startsWith("/session") || isPayerPage(path);

export const handleErrors = async (response,{ redirectOnError = true, signOutOnUnauthorized = redirectOnError } = {}) => {
    const actualResponse = response instanceof Promise ? await response : response;

    if (!actualResponse.ok) {
        try{
            window.analytics.track(`Error ${actualResponse?.statusText}`,{
                ...actualResponse
            })
        }catch{
            console.error("Segment Error");
        }
        !isWhiteListError(actualResponse) && Honeybadger.notify(actualResponse.error || `HTTP Error ${actualResponse?.status}`, {
            context: {
                error: {
                    status: actualResponse?.status,
                    statusText: actualResponse?.statusText || 'Unknown error'
                },
                url: actualResponse?.url
            },
        });
        // Scope note: the 401/403 redirects below honor the same `redirectOnError`
        // gate as the existing 404/503 redirects, so optional/background loads opt
        // out by using nonRedirectFetcher (redirectOnError:false). A 401 there means
        // the session is genuinely dead (app-wide signout is correct); a 403 from an
        // optional widget should use nonRedirectFetcher so it doesn't yank the page.
        // `signOutOnUnauthorized` splits the 401 off from that gate for callers that
        // must stay on the page but should still sign out a dead session (CSV export).

        // A 401 means the backend rejected our bearer token (expired/revoked) even
        // though the NextAuth client session may still look authenticated — so
        // AuthManager's onUnauthenticated never fires. Clear the stale session and
        // bounce to signin, preserving where the user was so they return after login.
        if (
            actualResponse?.status === 401 &&
            signOutOnUnauthorized &&
            !isHandlingUnauthorized &&
            typeof window !== "undefined"
        ) {
            const path = window.location.pathname;
            if (!isPublicPage(path)) {
                isHandlingUnauthorized = true;
                const redirectPath = `${window.location.pathname}${window.location.search}`;
                // signOut navigates on success. If it rejects before navigating,
                // re-arm the guard so a later 401 can retry rather than being dead
                // for the rest of the page's life.
                signOut({ callbackUrl: `/session/signin?redirectPath=${encodeURIComponent(redirectPath)}` })
                    .catch(() => {
                        isHandlingUnauthorized = false;
                    });
                throw await parseJsonBody(actualResponse);
            }
        }
        // A 403 means the user IS authenticated but lacks permission for this
        // resource/page. Don't sign them out — route them to the first tab their
        // permissions allow (mirrors AuthManager's client-side route gate, but for
        // pages that only surface the permission failure via the API).
        if (
            actualResponse?.status === 403 &&
            redirectOnError &&
            !isHandlingForbidden &&
            typeof window !== "undefined"
        ) {
            const path = window.location.pathname;
            if (!isPublicPage(path)) {
                isHandlingForbidden = true;
                getSession()
                    .then((session) => {
                        // No session => this is really an auth (401) situation, not a
                        // permission one; leave it alone.
                        if (!session?.user) {
                            isHandlingForbidden = false;
                            return;
                        }
                        const links = filterLinksFromPermissions(session.user.permissions);
                        const firstAllowed = links.find((link) => link.href)?.href;
                        // Exact path match, NOT startsWith: the first allowed tab is
                        // usually "/" (Dashboard), and every path startsWith("/"), so
                        // startsWith would never navigate. Exact equality also breaks
                        // the stale-permissions loop — if "/" itself 403s we land back
                        // here already on "/", match, and stop instead of re-redirecting.
                        if (firstAllowed && window.location.pathname !== firstAllowed) {
                            window.location.href = firstAllowed;
                        } else {
                            // Already on the only page they can reach — don't loop.
                            isHandlingForbidden = false;
                        }
                    })
                    .catch(() => {
                        isHandlingForbidden = false;
                    });
                throw await parseJsonBody(actualResponse);
            }
        }
        // Some callers (e.g. optional sub-resources) want to handle failures
        // locally instead of bouncing the whole app to /404 or /503.
        if (redirectOnError && window.location.href.indexOf("/payments") === -1) {
            if (actualResponse?.status === 404) {
                window.location.href = "/404";
            }
            if (actualResponse?.status === 503) {
                window.location.href = "/503";
            }
        }
        throw await parseJsonBody(actualResponse);
    }
    return parseJsonBody(actualResponse);
};

// Process and display errors on the NodeJS layer
export const handleServerSideErrors = async (response) => {
    const [status, data] = await response;
    if (status >= 300) { // error case
        !isWhiteListError({status}) && Honeybadger.notify(data?.message || `HTTP Error ${status}`, {
            context: {
                error: {
                    status,
                    statusText: data?.message || 'Unknown error'
                },
            },
        });
        return [null, data];
    }
    return [data, null];
};

export const handleFileDownloadErrors = async (response) => {
    if (!response.ok) {
        !isWhiteListError(response) && Honeybadger.notify(response.error || `HTTP Error ${response?.status}`, {
            context: {
                error: {
                    status: response?.status,
                    statusText: response?.statusText || 'Unknown error'
                },
                url: response?.url
            },
        });
        return response.blob().then(Promise.reject.bind(Promise));
    }
    return response.blob();
};


export const silentFetcher = (url) => fetch(url).then(handleErrors);

const FORBIDDEN = 403;
// True for a 403, whether given the response or what a fetcher threw for it
export const isRefusal = (responseOrError) => responseOrError?.status === FORBIDDEN;
// What a fetcher throws for a 403 it keeps on the page
export const refusal = () => ({ type: 'error', status: FORBIDDEN, message: GENERIC_ERROR_MESSAGE });

// For a load the page can do without: a label, a banner, a count, a search. A 403 there means this
// teammate can't see that part, not that they're on the wrong page, so it stays put and throws
// with `status` set for the caller to hide that part. Anything else goes as with silentFetcher.
// Use it through useBackgroundSWR, which adds the retry rule that goes with it.
export const backgroundFetcher = async (url) => {
    const response = await fetch(url);
    if (isRefusal(response)) throw refusal();
    return handleErrors(response);
};

// Like silentFetcher, but never triggers a full-page redirect on 404/503.
// Use for optional resources whose absence the component handles gracefully.
export const nonRedirectFetcher = (url) => fetch(url).then((response) => handleErrors(response, { redirectOnError: false }));
// The same, except that a 401 (the session has expired) signs out instead of showing as a failure.
export const signOutOnUnauthorizedFetcher = (url) => fetch(url)
    .then((response) => handleErrors(response, { redirectOnError: false, signOutOnUnauthorized: true }));

// The sentence to show for a failed load. nonRedirectFetcher throws the parsed backend body, so a
// merchant's own sentence ("Please re-link your account.") is on it, and a fixed line telling them
// to refresh would hide the one thing that fixes it. A network failure is an Error whose message
// is for developers, so only a parsed body is trusted.
// A Rails `errors: ["..."]` reaches the caller as that bare string, so a string is a sentence too.
export const failureSentence = (error, fallback) => {
    if (typeof error === 'string') return error.trim() ? error : fallback;
    return error && !(error instanceof Error) && typeof error.message === 'string' && error.message.trim()
        ? error.message
        : fallback;
};

// The reason a failure gives, to add after our own sentence about it, or nothing. Our generic
// message counts as nothing: it explains no more than the "try again" next to it.
export const failureReason = (error) => {
    const sentence = failureSentence(error, GENERIC_ERROR_MESSAGE);
    return sentence === GENERIC_ERROR_MESSAGE ? undefined : sentence;
};
